import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTasks, useDeleteTask, useAllUsers, useItems, useUserViews, useCreateView, useUpdateView, useDeleteView, useSetDefaultView, useCurrentUser } from '../hooks/useQueries';
import ListViewPage from '../components/Layout/ListViewPage';
import Modal from '../components/Popup/Modal';
import CreateTaskModal from '../components/CreateTaskModal';
import BulkActionsPopup from '../components/Popup/BulkActionsPopup';
import StatusBadge from '../components/Popup/StatusBadge';
import { formatDate, isOverdue, isDueSoon, formatDueDateRelative } from '../utils/dateUtils';
import { useIsMobile } from '../hooks/useIsMobile';
import { useColumnWidths } from '../hooks/useColumnWidths';
import { formatItemRef, matchesItemId } from '../utils/itemIdFormat';
import './tasks.css';

// In-memory view selection for this runtime only (resets on full page refresh).
let tasksRuntimeViewSelection = null;

// Icon components for filters
const FunnelIcon = () => <i className="bi bi-funnel"></i>;
const PersonIcon = () => <i className="bi bi-person"></i>;
const FolderIcon = () => <i className="bi bi-folder"></i>;

const normalizeMultiFilter = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value) return [value];
  return [];
};

export default function TaskListView() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  
  // Check if we're on the /my-tasks route
  const isMyTasksRoute = location.pathname === '/my-tasks';

  const listStateStorageKey = `tasks-list-state:${location.pathname}`;
  const initialListState = useMemo(() => {
    try {
      const raw = sessionStorage.getItem(listStateStorageKey);
      if (!raw) return { _hasState: false };
      const parsed = JSON.parse(raw);
      return { _hasState: true, ...parsed };
    } catch (error) {
      console.warn('Failed to load tasks list state:', error);
      return { _hasState: false };
    }
  }, [listStateStorageKey]);
  
  const [searchTerm, setSearchTerm] = useState(initialListState.searchTerm || '');
  const [sortConfig, setSortConfig] = useState(initialListState.sortConfig || { key: 'due_date', direction: 'asc' });
  const [statusFilter, setStatusFilter] = useState(() => normalizeMultiFilter(initialListState.statusFilter));
  const [assignedToFilter, setAssignedToFilter] = useState(() => normalizeMultiFilter(initialListState.assignedToFilter));
  const [assignedToUserIdFilter, setAssignedToUserIdFilter] = useState(initialListState.assignedToUserIdFilter || '');
  const [projectFilter, setProjectFilter] = useState(() => normalizeMultiFilter(initialListState.projectFilter));
  const [selectedRows, setSelectedRows] = useState(() => new Set());
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [bulkDeleteModal, setBulkDeleteModal] = useState({ show: false, selectedRows: new Set() });
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [deleteModal, setDeleteModal] = useState({ show: false, task: null });
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  
  // Context menu state
  const [contextMenu, setContextMenu] = useState({
    show: false,
    x: 0,
    y: 0,
    task: null
  });
  
  // View management state
  const [currentView, setCurrentView] = useState('default');
  const [visibleColumns, setVisibleColumns] = useState(['id', 'parent_id', 'description', 'due_date', 'assigned_to', 'status', 'created_on', 'completed_on']);
  const [deleteViewModal, setDeleteViewModal] = useState({ show: false, view: null });

  // React Query hooks
  // IMPORTANT: keep list search/filtering client-side (like Items/Approvals)
  // so typing in the search box does not refetch tasks from the server.
  const { data: allTasks = [], isLoading: loading, error } = useTasks();
  const { data: users = [] } = useAllUsers();
  const { data: items = [] } = useItems();
  const { data: currentUser } = useCurrentUser();

  const userNameById = useMemo(() => {
    const map = new Map();
    for (const user of users || []) {
      if (!user) continue;
      if (user.id == null) continue;
      map.set(String(user.id), user.name || user.email || String(user.id));
    }
    return map;
  }, [users]);

  const getAssigneeDisplay = useCallback((task) => {
    if (!task) return '';
    if (task.assigned_to_user_name) return task.assigned_to_user_name;

    const candidateId = task.assigned_to_user_id ?? task.assigned_to;
    if (candidateId != null) {
      const mapped = userNameById.get(String(candidateId));
      if (mapped) return mapped;
    }

    if (task.assigned_to == null) return '';
    return String(task.assigned_to);
  }, [userNameById]);
    // Persist search + dropdown filter state so returning from detail pages isn't jarring.
    // The selected view is stored alongside it: the snapshot is only a valid stand-in
    // for the view it was captured under.
    React.useEffect(() => {
      try {
        const stateToSave = {
          currentView: String(currentView),
          searchTerm,
          sortConfig,
          statusFilter,
          assignedToFilter,
          assignedToUserIdFilter,
          projectFilter
        };
        sessionStorage.setItem(listStateStorageKey, JSON.stringify(stateToSave));
      } catch (error) {
        console.warn('Failed to save tasks list state:', error);
      }
    }, [listStateStorageKey, currentView, searchTerm, sortConfig, statusFilter, assignedToFilter, assignedToUserIdFilter, projectFilter]);

    // Re-apply a saved snapshot wholesale. Needed because the same component instance
    // backs both /tasks and /my-tasks, so filter state carries across the routes even
    // though each route stores its own snapshot.
    const applyListStateSnapshot = useCallback((snapshot) => {
      setSearchTerm(snapshot.searchTerm || '');
      setSortConfig(snapshot.sortConfig || { key: 'due_date', direction: 'asc' });
      setStatusFilter(normalizeMultiFilter(snapshot.statusFilter));
      setAssignedToFilter(normalizeMultiFilter(snapshot.assignedToFilter));
      setAssignedToUserIdFilter(snapshot.assignedToUserIdFilter || '');
      setProjectFilter(normalizeMultiFilter(snapshot.projectFilter));
    }, []);

    // Ensure /my-tasks always filters to the current user.
    React.useEffect(() => {
      if (!isMyTasksRoute) return;
      if (!currentUser?.id) return;
      const userIdStr = String(currentUser.id);
      if (assignedToUserIdFilter === userIdStr) return;
      setAssignedToUserIdFilter(userIdStr);
    }, [isMyTasksRoute, currentUser?.id, assignedToUserIdFilter]);

  const deleteTaskMutation = useDeleteTask();
  
  // View management hooks
  const { data: userViews = [] } = useUserViews('tasks');
  const createViewMutation = useCreateView();
  const updateViewMutation = useUpdateView();
  const deleteViewMutation = useDeleteView();
  const setDefaultViewMutation = useSetDefaultView();

  // Column widths are saved per view (and mirrored to localStorage so the
  // built-in "All Tasks" view remembers them too).
  const { columnWidths, handleColumnResize } = useColumnWidths({
    tableName: 'tasks',
    currentView,
    userViews
  });

  const showToast = useCallback((message, type = 'success') => {
    setToast({ show: true, message, type });
  }, []);

  const handleCloseToast = useCallback(() => {
    setToast({ show: false, message: '', type: 'success' });
  }, []);

  // Context menu handlers
  const handleContextMenu = useCallback((event, task) => {
    event.preventDefault();
    event.stopPropagation();

    if (isMobile) {
      // On mobile, show at bottom as sheet
      setContextMenu({
        show: true,
        x: 0,
        y: window.innerHeight,
        task: task
      });
    } else {
      // Desktop: show at cursor position
      setContextMenu({
        show: true,
        x: event.clientX,
        y: event.clientY,
        task: task
      });
    }
  }, [isMobile]);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu({ show: false, x: 0, y: 0, task: null });
  }, []);

  const handleContextMenuAction = useCallback((action, task) => {
    handleCloseContextMenu();
    
    switch (action) {
      case 'view':
        navigate(`/tasks/${task.id}`);
        break;
      case 'viewParent':
        if (task.parent_id) {
          navigate(`/items/${task.parent_id}?source=tasks`);
        } else {
          showToast('No parent item found for this task', 'warning');
        }
        break;
      case 'delete':
        setDeleteModal({ show: true, task });
        break;
      default:
        break;
    }
  }, [navigate, showToast, handleCloseContextMenu]);

  // Close context menu when clicking elsewhere
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (contextMenu.show) {
        handleCloseContextMenu();
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextMenu.show]);

  // Handle view selection based on route
  React.useEffect(() => {
    if (userViews.length === 0) return;

    // Saved list state stands in for the view it was captured under, and only that
    // view. When the view being applied is a different one -- e.g. a refresh drops the
    // runtime selection and we fall back to All Tasks while the snapshot still holds
    // My Tasks' assignee filter -- the view's own filters have to win, or the list
    // renders filtered by a view it is no longer showing.
    const applyView = (targetView) => {
      const targetViewId = String(targetView);
      const snapshotMatchesView = initialListState._hasState === true
        && String(initialListState.currentView ?? '') === targetViewId;

      handleViewChange(targetViewId, { preserveListState: snapshotMatchesView });
      if (snapshotMatchesView) {
        applyListStateSnapshot(initialListState);
      }
    };

    // If we're on /my-tasks route, always show My Tasks view
    if (isMyTasksRoute) {
      const myTasksView = userViews.find(view => view.view_name === 'My Tasks');
      if (myTasksView) {
        // Use applyView to properly apply all filters and settings
        applyView(myTasksView.id.toString());
      }
      return;
    }

    // Prefer runtime in-memory selection during this app session.
    const runtimeViewId = tasksRuntimeViewSelection;
    const hasRuntimeCustomView = runtimeViewId && runtimeViewId !== 'default' &&
      userViews.some(view => String(view?.id) === String(runtimeViewId));
    const defaultUserView = userViews.find(view => view?.is_default === true);

    if (hasRuntimeCustomView) {
      // Preserve explicit runtime selection when navigating pages.
      applyView(runtimeViewId);
    } else if (runtimeViewId === 'default') {
      applyView('default');
    } else if (defaultUserView) {
      // On refresh/new app session, fall back to DB default view.
      applyView(String(defaultUserView.id));
    } else {
      // If no saved view, apply default
      applyView('default');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userViews, isMyTasksRoute, initialListState]);

  // Split tasks into active and completed based on status
  const { activeTasks, completedTasks } = useMemo(() => {
    const completedStatuses = ['Completed', 'Canceled'];
    
    const active = [];
    const completed = [];
    
    allTasks.forEach(task => {
      if (completedStatuses.includes(task.status)) {
        completed.push(task);
      } else {
        active.push(task);
      }
    });
    
    return { activeTasks: active, completedTasks: completed };
  }, [allTasks]);

  // Get unique values for filters
  const uniqueStatuses = useMemo(() => {
    const statuses = allTasks.map(task => task.status).filter(Boolean);
    return [...new Set(statuses)].sort();
  }, [allTasks]);

  const uniqueAssignees = useMemo(() => {
    const assignees = allTasks
      .map(task => getAssigneeDisplay(task))
      .filter(Boolean);
    return [...new Set(assignees)].sort();
  }, [allTasks, getAssigneeDisplay]);

  const uniqueProjects = useMemo(() => {
    const projects = allTasks.map(task => task.parent_title).filter(Boolean);
    return [...new Set(projects)].sort();
  }, [allTasks]);

  // Create options for react-select
  const statusOptions = useMemo(() => 
    uniqueStatuses.map(status => ({ value: status, label: status })), 
    [uniqueStatuses]
  );

  const assigneeOptions = useMemo(() => 
    uniqueAssignees.map(assignee => ({ value: assignee, label: assignee })), 
    [uniqueAssignees]
  );

  const projectOptions = useMemo(() => 
    uniqueProjects.map(project => ({ value: project, label: project })), 
    [uniqueProjects]
  );

  // Custom filtering logic (client-side filters not handled by server)
  const customFilter = (data) => {
    let filtered = data;

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(task => (
        task.id?.toString().includes(searchTerm) ||
        matchesItemId(task.parent_id, searchTerm) ||
        task.description?.toLowerCase().includes(searchLower) ||
        getAssigneeDisplay(task)?.toLowerCase().includes(searchLower) ||
        task.parent_title?.toLowerCase().includes(searchLower) ||
        task.project_name?.toLowerCase().includes(searchLower) ||
        task.status?.toLowerCase().includes(searchLower)
      ));
    }

    // Apply status filter
    if (statusFilter.length > 0) {
      const allowedStatuses = new Set(statusFilter);
      filtered = filtered.filter(task => allowedStatuses.has(task.status));
    }

    // Apply assigned-to user ID filter (used by "My Tasks" view)
    if (assignedToUserIdFilter) {
      filtered = filtered.filter(task =>
        String(task.assigned_to_user_id || '') === String(assignedToUserIdFilter)
      );
    }

    // Apply assigned to filter (for display name filtering when not using user ID)
    if (assignedToFilter.length > 0 && !assignedToUserIdFilter) {
      const allowedAssignees = new Set(assignedToFilter);
      filtered = filtered.filter(task => allowedAssignees.has(getAssigneeDisplay(task)));
    }

    // Apply project filter
    if (projectFilter.length > 0) {
      const allowedProjects = new Set(projectFilter);
      filtered = filtered.filter(task => allowedProjects.has(task.parent_title));
    }

    return filtered;
  };

  // Task priority styling
  const getPriorityIcon = (task) => {
    if (isOverdue(task.due_date) && task.status !== 'Completed') {
      return <i className="bi bi-exclamation-triangle-fill text-danger" title="Overdue"></i>;
    }
    if (isDueSoon(task.due_date) && task.status !== 'Completed') {
      return <i className="bi bi-exclamation-triangle-fill text-warning" title="Due Soon"></i>;
    }
    return null;
  };

  // Handle task deletion
  const handleDeleteTask = async (task) => {
    try {
      await deleteTaskMutation.mutateAsync(task.id);
      showToast('Task moved to trash', 'success');
      setDeleteModal({ show: false, task: null });
    } catch (error) {
      console.error('Error deleting task:', error);
      showToast('Failed to delete task', 'error');
    }
  };

  // View management handlers
  const handleViewChange = (view, { preserveListState = false } = {}) => {
    if (!isMyTasksRoute) {
      tasksRuntimeViewSelection = String(view);
    }

    if (view === 'default') {
      setCurrentView('default');
      setVisibleColumns(['id', 'parent_id', 'description', 'due_date', 'assigned_to', 'status', 'created_on', 'completed_on']);
      if (!preserveListState) {
        setStatusFilter([]);
        setAssignedToFilter([]);
        setAssignedToUserIdFilter('');
        setProjectFilter([]);
        setSortConfig({ key: 'due_date', direction: 'asc' });
      }
    } else {
      const selectedView = Array.isArray(userViews) ? userViews.find(v => v && v.id === parseInt(view)) : null;
      if (selectedView) {
        setCurrentView(view);
        setVisibleColumns(selectedView.visible_columns || ['id', 'parent_id', 'description', 'due_date', 'assigned_to', 'status', 'created_on', 'completed_on']);
        if (!preserveListState) {
          // Apply the view's filters. Every filter is assigned unconditionally so a
          // view that omits one clears it rather than inheriting it from the previously
          // selected view.
          const viewFilters = selectedView.filters || {};
          setStatusFilter(normalizeMultiFilter(viewFilters.status));
          setProjectFilter(normalizeMultiFilter(viewFilters.project));

          // Handle both old assigned_to and new assigned_to_user_id filters. The id is
          // stored on the view itself, so it must not wait on currentUser loading --
          // only the dropdown's display name needs the user record.
          if (viewFilters.assigned_to_user_id) {
            setAssignedToUserIdFilter(viewFilters.assigned_to_user_id);
            const isCurrentUser = currentUser
              && String(viewFilters.assigned_to_user_id) === String(currentUser.id);
            setAssignedToFilter(isCurrentUser ? [currentUser.name] : []);
          } else {
            setAssignedToFilter(normalizeMultiFilter(viewFilters.assigned_to));
            setAssignedToUserIdFilter('');
          }

          // Apply saved sort
          setSortConfig(selectedView.sort_config || { key: 'due_date', direction: 'asc' });
        }
      }
    }
  };

  const handleSaveView = async (viewData, editingView) => {
    console.log('handleSaveView called with:');
    console.log('viewData:', viewData);
    console.log('editingView:', editingView);
    
    const viewPayload = {
      ...viewData,
      table_name: 'tasks'
    };

    // Carry the current column widths onto the view being saved, but only when
    // those widths belong to it: creating a view, or editing the active one.
    // Global views never store widths - they are shared, so widths stay per-user
    // in localStorage (see useColumnWidths).
    const widthsBelongToTargetView = !editingView || parseInt(currentView, 10) === editingView.id;
    if (widthsBelongToTargetView && !viewPayload.is_global && !editingView?.is_global) {
      viewPayload.column_widths = columnWidths;
    }

    console.log('viewPayload:', viewPayload);

    try {
      if (editingView) {
        console.log('Updating view with ID:', editingView.id);
        const updateData = {
          id: editingView.id,
          ...viewPayload
        };
        console.log('updateData:', updateData);
        
        const result = await updateViewMutation.mutateAsync(updateData);
        console.log('Update result:', result);
        
        // Update current view state to reflect the saved changes
        if (parseInt(currentView) === editingView.id) {
          setVisibleColumns(viewData.visible_columns);
          if (viewData.filters) {
            setStatusFilter(normalizeMultiFilter(viewData.filters.status));
            setAssignedToFilter(normalizeMultiFilter(viewData.filters.assigned_to));
            setAssignedToUserIdFilter(viewData.filters.assigned_to_user_id || '');
            setProjectFilter(normalizeMultiFilter(viewData.filters.project));
          }
          if (viewData.sort_config) {
            setSortConfig(viewData.sort_config);
          }
        }
        
        showToast('View updated successfully');
      } else {
        const result = await createViewMutation.mutateAsync(viewPayload);
        if (!isMyTasksRoute) {
          tasksRuntimeViewSelection = String(result.id);
        }
        setCurrentView(result.id.toString());
        showToast('View created successfully');
      }
    } catch (error) {
      console.error('Error saving view:', error);
      showToast('Failed to save view', 'error');
    }
  };

  // The default view is a per-user preference stored separately from the view, so a
  // user can default to a global view they don't own. Clearing it (null) falls back
  // to the built-in "All Tasks" view.
  const handleSetDefaultView = async (view) => {
    try {
      if (view === 'default') {
        await setDefaultViewMutation.mutateAsync({ tableName: 'tasks', viewId: null });
        showToast('"All Tasks" is now your default view');
      } else if (view?.id) {
        await setDefaultViewMutation.mutateAsync({ tableName: 'tasks', viewId: view.id });
        showToast(`"${view.view_name}" is now your default view`);
      }
    } catch (error) {
      console.error('Error setting default view:', error);
      showToast('Failed to set default view', 'error');
    }
  };

  const handleDeleteView = (view) => {
    setDeleteViewModal({ show: true, view });
  };

  // Multiselect handlers
  const handleSelectionChange = useCallback((newSelection) => {
    setSelectedRows(newSelection instanceof Set ? newSelection : new Set());
  }, []);

  const handleBulkDelete = useCallback((selectionSet) => {
    if (!(selectionSet instanceof Set) || selectionSet.size === 0) {
      return;
    }
    setBulkDeleteModal({ show: true, selectedRows: selectionSet });
  }, []);

  const handleConfirmBulkDelete = useCallback(async () => {
    const { selectedRows: selectionSet } = bulkDeleteModal;
    if (!(selectionSet instanceof Set) || selectionSet.size === 0) {
      return;
    }

    setIsBulkDeleting(true);
    const ids = Array.from(selectionSet);

    try {
      await Promise.all(ids.map(id => deleteTaskMutation.mutateAsync(id)));
      showToast(`${ids.length} task${ids.length !== 1 ? 's' : ''} moved to trash`, 'success');
      setSelectedRows(new Set());
      setBulkDeleteModal({ show: false, selectedRows: new Set() });
    } catch (error) {
      console.error('Bulk delete failed:', error);
      showToast('Failed to delete selected tasks', 'error');
    } finally {
      setIsBulkDeleting(false);
    }
  }, [bulkDeleteModal, deleteTaskMutation, showToast]);

  const handleClearSelection = useCallback(() => {
    setSelectedRows(new Set());
  }, []);

  // Bulk actions configuration
  const bulkActions = useMemo(() => ([
    {
      label: 'Move to Trash',
      icon: 'bi bi-trash3',
      className: 'btn btn-danger',
      disabled: isBulkDeleting,
      onClick: handleBulkDelete
    }
  ]), [handleBulkDelete, isBulkDeleting]);

  const handleConfirmDeleteView = async () => {
    if (deleteViewModal.view) {
      try {
        await deleteViewMutation.mutateAsync(deleteViewModal.view.id);
        showToast('View deleted successfully');
        if (parseInt(currentView) === deleteViewModal.view.id) {
          handleViewChange('default');
        }
        setDeleteViewModal({ show: false, view: null });
      } catch (error) {
        showToast('Failed to delete view', 'error');
        console.error('Delete view error:', error);
      }
    }
  };

  // Define columns
  const columns = [
    {
      key: 'id',
      label: 'ID',
      render: (task) => `#${task.id}`
    },
    {
      key: 'parent_id',
      label: 'Parent Item',
      exportValue: (task) => {
        const idPart = task.parent_id ? formatItemRef(task.parent_id) : '-';
        const titlePart = task.parent_title ? ` - ${task.parent_title}` : '';
        return `${idPart}${titlePart}`;
      },
      render: (task) => (
        <div>
          <strong>{formatItemRef(task.parent_id)}</strong>
          {task.parent_title && (
            <div className="text-muted small">{task.parent_title}</div>
          )}
        </div>
      )
    },
    {
      key: 'description',
      label: 'Task Description',
      render: (task) => (
        <div style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {task.description}
        </div>
      )
    },
    {
      key: 'due_date',
      label: 'Due Date',
      exportValue: (task) => (task.due_date ? formatDate(task.due_date) : '-'),
      render: (task) => {
        if (!task.due_date) return '-';
        const formatted = formatDueDateRelative(task.due_date);
        const overdue = isOverdue(task.due_date) && task.status !== 'Completed';
        const dueSoon = isDueSoon(task.due_date) && task.status !== 'Completed';
        const priorityIcon = getPriorityIcon(task);
        
        return (
          <div className="d-flex align-items-center">
            {/* {priorityIcon} */}
            <span className={`${overdue ? 'text-danger fw-bold' : dueSoon ? 'text-warning fw-bold' : ''} ${priorityIcon ? 'ms-1' : ''}`}>
              {formatted}
            </span>
          </div>
        );
      }
    },
    {
      key: 'assigned_to',
      label: 'Assigned To',
      render: (task) => getAssigneeDisplay(task) || '-'
    },
    {
      key: 'status',
      label: 'Status',
      render: (task) => (
        <StatusBadge status={task.status} type="task" />
      )
    },
    {
      key: 'created_on',
      label: 'Created',
      render: (task) => formatDate(task.created_on)
    },
    {
      key: 'completed_on',
      label: 'Completed On',
      exportValue: (task) => (task.completed_on ? formatDate(task.completed_on) : '-'),
      render: (task) => (task.completed_on ? formatDate(task.completed_on) : '-')
    }
  ];

  // Handle assignee filter change - clear user ID when manually changed
  const handleAssigneeFilterChange = (selectedOptions) => {
    const values = Array.isArray(selectedOptions) ? selectedOptions.map(option => option.value) : [];
    setAssignedToFilter(values);
    // Clear user ID filter when manually selecting assignee
    const currentUserName = currentUser?.name || '';
    if (values.length !== 1 || values[0] !== currentUserName) {
      setAssignedToUserIdFilter('');
    }
  };

  // Filter options for SearchFilters component
  const filters = [
    {
      icon: FunnelIcon,
      value: statusOptions.filter(option => statusFilter.includes(option.value)),
      onChange: (selectedOptions) => setStatusFilter(Array.isArray(selectedOptions) ? selectedOptions.map(opt => opt.value) : []),
      options: statusOptions,
      placeholder: 'All statuses',
      isClearable: true,
      isMulti: true
    },
    {
      icon: PersonIcon,
      value: assigneeOptions.filter(option => assignedToFilter.includes(option.value)),
      onChange: handleAssigneeFilterChange,
      options: assigneeOptions,
      placeholder: 'All assignees',
      isClearable: true,
      isMulti: true
    },
    {
      icon: FolderIcon,
      value: projectOptions.filter(option => projectFilter.includes(option.value)),
      onChange: (selectedOptions) => setProjectFilter(Array.isArray(selectedOptions) ? selectedOptions.map(opt => opt.value) : []),
      options: projectOptions,
      placeholder: 'All projects',
      isClearable: true,
      isMulti: true
    }
  ];

  // Action buttons
  const actionButtons = [
    {
      label: 'Create Task',
      variant: 'primary',
      icon: 'bi-plus-lg',
      onClick: () => setShowCreateTaskModal(true)
    }
  ];

  const handleTaskRowClick = useCallback((event, task) => {
    const targetUrl = `/tasks/${task.id}`;
    if (event?.button === 1 || event?.metaKey || event?.ctrlKey) {
      window.open(targetUrl, '_blank', 'noopener');
      return;
    }

    navigate(targetUrl);
  }, [navigate]);

  return (
    <>
      <ListViewPage
        // Page configuration
        title="Tasks"
        itemLabel="tasks"
        tableName="tasks"
        
        // Data and loading
        activeData={activeTasks}
        completedData={completedTasks}
        loading={loading}
        error={error}
        
        // Columns configuration
        columns={columns}
        defaultVisibleColumns={visibleColumns}
        visibleColumns={visibleColumns}
        onToggleColumn={(column) => {
          setVisibleColumns(prev => 
            prev.includes(column) 
              ? prev.filter(col => col !== column)
              : [...prev, column]
          );
        }}
        
        // Search and filtering
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search tasks, descriptions, assignees..."
        filters={filters}
        searchActions={isMobile ? (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setShowCreateTaskModal(true)}
            aria-label="New Task"
            title="New Task"
          >
            <i className="bi bi-plus-circle"></i>
          </button>
        ) : null}
        
        // Sorting
        sortConfig={sortConfig}
        onSort={setSortConfig}
        
        // View management
        useViews={true}
        userViews={userViews}
        currentView={currentView}
        onViewChange={handleViewChange}
        onSaveView={handleSaveView}
        onDeleteView={handleDeleteView}
        onSetDefaultView={handleSetDefaultView}

        // Actions and handlers
        onRowClick={handleTaskRowClick}
        onRowRightClick={handleContextMenu}
        actionButtons={actionButtons}
        
        // Split view configuration
        completedSectionTitle="Completed Tasks"
        completedSectionIcon="bi-check-circle"
        showCompletedByDefault={false}
        
        // Export functionality
        enableExport={true}
        exportFileName="tasks"
        exportButtonPosition="afterActions"
        
        // Custom filtering
        customFilter={customFilter}
        
        // Multi-select functionality
        enableMultiSelect={true}
        selectedRows={selectedRows}
        onSelectionChange={handleSelectionChange}
        selectableRowKey="id"
        bulkActions={bulkActions}
        
        // Modals and notifications
        toast={toast}
        onCloseToast={handleCloseToast}

        // Virtualization (performance)
        virtualizeActiveTable={true}
        virtualizeCompletedTable={true}

        // Resizable columns (widths persist with the selected view)
        enableColumnResize={true}
        columnWidths={columnWidths}
        onColumnResize={handleColumnResize}
      />

      {/* Create Task Modal */}
      <CreateTaskModal
        show={showCreateTaskModal}
        onHide={() => setShowCreateTaskModal(false)}
        onSuccess={(newTask) => {
          setShowCreateTaskModal(false);
          showToast('Task created successfully', 'success');
          if (newTask?.parent_id) {
            navigate(`/task/${newTask.id}`);
          }
        }}
        items={items}
        users={users}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        show={deleteModal.show}
        onCancel={() => setDeleteModal({ show: false, task: null })}
        onConfirm={() => handleDeleteTask(deleteModal.task)}
        title="Move Task to Trash"
        confirmText="Move to Trash"
        cancelText="Cancel"
        confirmBtnClass="btn-danger"
        loading={deleteTaskMutation.isPending}
        content={
          <div>
            <p>
              Are you sure you want to delete task <strong>#{deleteModal.task?.id}</strong>?
            </p>
            {deleteModal.task?.description && (
              <div className="mb-3">
                <strong>Task Description:</strong>
                <div className="text-muted">{deleteModal.task.description}</div>
              </div>
            )}
            <div className="alert alert-info">
              <i className="bi bi-info-circle me-2"></i>
              The task will be moved to the trashcan where it can be restored within 30 days before being permanently deleted.
            </div>
          </div>
        }
      />

      {/* Delete View Modal */}
      <Modal
        show={deleteViewModal.show}
        onCancel={() => setDeleteViewModal({ show: false, view: null })}
        onConfirm={handleConfirmDeleteView}
        title="Delete View"
        confirmText="Delete"
        cancelText="Cancel"
        confirmBtnClass="btn-danger"
        loading={deleteViewMutation.isPending}
        content={
          <div>
            <p>
              Are you sure you want to delete the view <strong>{deleteViewModal.view?.view_name}</strong>?
            </p>
            <div className="alert alert-warning">
              <i className="bi bi-exclamation-triangle me-2"></i>
              This action cannot be undone.
            </div>
          </div>
        }
      />

      {/* Bulk Actions Popup */}
      <BulkActionsPopup
        selectedRows={selectedRows}
        bulkActions={bulkActions}
        onClose={handleClearSelection}
        itemLabel="task"
      />

      {/* Bulk Delete Modal */}
      <Modal
        show={bulkDeleteModal.show}
        title="Confirm Bulk Delete"
        onCancel={() => setBulkDeleteModal({ show: false, selectedRows: new Set() })}
        onConfirm={handleConfirmBulkDelete}
        confirmText="Move to Trash"
        cancelText="Cancel"
        confirmBtnClass="btn-danger"
        loading={isBulkDeleting}
        content={
          <div className="bulk-delete-modal-content">
            <div className="alert alert-warning">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              <strong>Note:</strong> These tasks will be moved to the trashcan where they can be recovered for 30 days.
            </div>
            <p>Are you sure you want to delete {bulkDeleteModal.selectedRows.size} selected task{bulkDeleteModal.selectedRows.size !== 1 ? 's' : ''}?</p>
          </div>
        }
      />

      {/* Context Menu */}
      {contextMenu.show && isMobile && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 999
          }}
          onClick={handleCloseContextMenu}
        />
      )}

      {contextMenu.show && (
        <div 
          className="task-context-menu"
          style={{
            position: 'fixed',
            ...(isMobile ? {
              bottom: 0,
              left: 0,
              right: 0,
              top: 'auto'
            } : {
              top: contextMenu.y,
              left: contextMenu.x
            }),
            zIndex: 1000
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="context-menu-content">
            <button
              className="context-menu-item"
              onClick={() => handleContextMenuAction('view', contextMenu.task)}
            >
              <i className="bi bi-eye"></i>
              <span>View Task</span>
            </button>
            <button
              className="context-menu-item"
              onClick={() => handleContextMenuAction('viewParent', contextMenu.task)}
              disabled={!contextMenu.task?.parent_id}
            >
              <i className="bi bi-box-arrow-up-right"></i>
              <span>View Parent</span>
            </button>
            <div className="context-menu-divider"></div>
            <button
              className="context-menu-item context-menu-item-danger"
              onClick={() => handleContextMenuAction('delete', contextMenu.task)}
            >
              <i className="bi bi-trash"></i>
              <span>Delete Task</span>
            </button>
          </div>
        </div>
      )}
    </>
  );
}